import React, { useState } from 'react';
import { Alert, Box, Pagination, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  EmptySectionMessage,
  FullPageLoader,
} from '@tassomutuo-backoffice/ui';
import {
  AnyExternalLead,
  CdqExternalLead,
  ExternalLeadListParams,
  ExternalLeadProduct,
  LoanExternalLead,
  MortgageExternalLead,
  PaginatedResponse,
} from '@tassomutuo-backoffice/types';
import {
  listCdqExternalLeads,
  listLoanExternalLeads,
  listMortgageExternalLeads,
} from '../../../services/api/external-leads/external-leads';
import { useExternalLeadsParams } from './hooks/useExternalLeadsParams';
import ExternalLeadsFiltersBar from './components/ExternalLeadsFiltersBar';
import ExternalLeadsSortSelect from './components/ExternalLeadsSortSelect';
import ExternalLeadCardMortgage from './components/ExternalLeadCardMortgage';
import ExternalLeadCardLoan from './components/ExternalLeadCardLoan';
import ExternalLeadCardCdq from './components/ExternalLeadCardCdq';
import ExternalLeadDetailModal from './components/ExternalLeadDetailModal';

interface IProps {
  product: ExternalLeadProduct;
}

const QUERY_KEY = 'externalLeads';

const fetcher = async (
  product: ExternalLeadProduct,
  params: ExternalLeadListParams
): Promise<PaginatedResponse<AnyExternalLead>> => {
  if (product === 'mortgage') return listMortgageExternalLeads(params);
  if (product === 'loan') return listLoanExternalLeads(params);
  return listCdqExternalLeads(params);
};

const isUpstreamError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: number | string; message?: string };
  if (e.code === 502 || e.code === 504) return true;
  if (typeof e.message === 'string') {
    return ['upstream_unavailable', 'upstream_error', 'upstream_auth_error', 'upstream_timeout'].includes(
      e.message
    );
  }
  return false;
};

const ExternalLeadsList = ({ product }: IProps) => {
  const {
    state,
    apiParams,
    setQ,
    setStatus,
    setCreatedFrom,
    setCreatedTo,
    setSortBy,
    setPage,
    resetFilters,
  } = useExternalLeadsParams();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { isPending, data, error, refetch, isFetching } = useQuery<
    PaginatedResponse<AnyExternalLead>
  >({
    queryKey: [QUERY_KEY, product, apiParams],
    queryFn: () => fetcher(product, apiParams),
    refetchOnWindowFocus: false,
  });

  const results = data?.results ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalResults = data?.totalResults ?? 0;

  const renderCard = (lead: AnyExternalLead) => {
    if (product === 'mortgage') {
      return (
        <ExternalLeadCardMortgage
          key={lead.id}
          lead={lead as MortgageExternalLead}
          onOpenDetails={setSelectedId}
        />
      );
    }
    if (product === 'loan') {
      return (
        <ExternalLeadCardLoan
          key={lead.id}
          lead={lead as LoanExternalLead}
          onOpenDetails={setSelectedId}
        />
      );
    }
    return (
      <ExternalLeadCardCdq
        key={lead.id}
        lead={lead as CdqExternalLead}
        onOpenDetails={setSelectedId}
      />
    );
  };

  const showUpstreamBanner = error && isUpstreamError(error);
  const showGenericError = error && !showUpstreamBanner;

  return (
    <Box>
      <FullPageLoader show={isPending} />

      <ExternalLeadsFiltersBar
        product={product}
        q={state.q}
        status={state.status}
        createdFrom={state.createdFrom}
        createdTo={state.createdTo}
        onChangeQ={setQ}
        onChangeStatus={setStatus}
        onChangeCreatedFrom={setCreatedFrom}
        onChangeCreatedTo={setCreatedTo}
        onReset={resetFilters}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          pt: 3,
          pb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {data
            ? `${totalResults} lead totali${isFetching ? ' · aggiornamento…' : ''}`
            : ' '}
        </Typography>
        <ExternalLeadsSortSelect value={state.sortBy} onChange={setSortBy} />
      </Box>

      {showUpstreamBanner && (
        <Box px={4} pb={2}>
          <Alert
            severity="warning"
            action={
              <button
                onClick={() => refetch()}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Riprova
              </button>
            }
          >
            Servizio temporaneamente non disponibile. Riprova tra qualche istante.
          </Alert>
        </Box>
      )}

      {showGenericError && (
        <Box px={4} pb={2}>
          <Alert severity="error">
            {(error as { message?: string })?.message
              ? `Errore: ${(error as { message?: string }).message}`
              : 'Si è verificato un errore durante il caricamento.'}
          </Alert>
        </Box>
      )}

      {!isPending && !error && results.length === 0 && (
        <EmptySectionMessage title="Nessuna lead esterna trovata con i filtri attuali" />
      )}

      <Stack>{results.map(renderCard)}</Stack>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <Pagination
            page={state.page}
            count={totalPages}
            color="primary"
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      )}

      <ExternalLeadDetailModal
        open={!!selectedId}
        product={product}
        leadId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </Box>
  );
};

export default ExternalLeadsList;
